import * as React from 'react';
import { renderLinkedText } from '../../../helpers/renderLinkedText';
import type { TextBlock } from '../../../interfaces/textEditor.interface';
import { ImageWithLoader } from '../../ImageWithLoader';

// TRIL-275: read-only renderer for a post's text blocks, extracted verbatim
// from UserPost.component so the version-history view renders each version's
// blocks identically to the live post.
export const renderPostBlock = (block: TextBlock): React.ReactNode => {
  switch (block.type) {
    case 'Heading': {
      const level = block.data?.level ?? 1;
      const HeadingTag = `h${level}` as React.ElementType;
      return (
        <HeadingTag className={'POST__HEADER'}>{block.data?.header}</HeadingTag>
      );
    }
    case 'Paragraph':
      return <p>{renderLinkedText(block.data?.paragraph)}</p>;
    case 'List':
      return block.data?.style === 'ordered' ? (
        <ol style={{ paddingLeft: '2rem', listStyleType: 'decimal' }}>
          {/* biome-ignore lint/suspicious/noExplicitAny: editor item shape, matches UserPost */}
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ol>
      ) : (
        <ul style={{ paddingLeft: '2rem', listStyleType: 'disc' }}>
          {/* biome-ignore lint/suspicious/noExplicitAny: editor item shape, matches UserPost */}
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ul>
      );
    case 'Image':
      return (
        <ImageWithLoader
          src={block.data?.file?.url as string}
          alt="Post Image"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      );
    case 'Code':
      return (
        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <code>{block.data?.code}</code>
        </pre>
      );
    case 'Quote':
      return (
        <blockquote
          style={{
            borderLeft: '4px solid var(--accent-emeraude)',
            paddingLeft: '1rem',
            margin: '1rem 0',
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
          }}
        >
          <p style={{ margin: 0 }}>{renderLinkedText(block.data?.paragraph)}</p>
          {block.data?.caption && (
            <p
              style={{
                margin: '0.5rem 0 0',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
              }}
            >
              — {block.data.caption}
            </p>
          )}
        </blockquote>
      );
    default:
      return null;
  }
};

const PostBlocks = ({ blocks }: { blocks: TextBlock[] }) => (
  <>
    {(blocks ?? []).map((block, index) => (
      <React.Fragment key={`${block.id}-${index}`}>
        {renderPostBlock(block)}
      </React.Fragment>
    ))}
  </>
);

export default PostBlocks;
